import { useQuery } from '@tanstack/react-query'
import {
  LineChart, Line,
  BarChart, Bar, Cell,
  PieChart, Pie,
  XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts'
import api from '@/lib/api'
import { Card, Skeleton } from '@/components/ui'
import { cn } from '@/lib/utils'
import type { ChartCardDef, ChartDataPoint } from './chartCardRegistry'

const DONUT_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6']

interface Props {
  def: ChartCardDef
  className?: string
}

export function ChartTile({ def, className }: Props) {
  const { data: rawData, isLoading, error } = useQuery({
    queryKey: ['chart', def.key],
    queryFn: () => api.get(def.endpoint).then((r) => r.data as unknown),
    staleTime: 5 * 60 * 1000,
  })

  const chartData: ChartDataPoint[] = rawData != null ? def.transform(rawData) : []
  const fmt = def.valueFormatter ?? ((v: number) => String(v))

  return (
    <Card className={cn('h-full', className)}>
      <Card.Body className="h-full flex flex-col p-4 gap-2">
        <p className="text-small font-medium text-gray-700 shrink-0">{def.title}</p>

        {isLoading ? (
          <div className="flex-1">
            <Skeleton className="h-full w-full rounded" />
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-xs text-red-500 text-center px-2">
              Daten nicht verfügbar
            </p>
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-xs text-gray-400">Keine Daten vorhanden</p>
          </div>
        ) : def.chartType === 'line' ? (
          <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={fmt}
                />
                <Tooltip
                  cursor={{ stroke: 'rgba(0,0,0,0.06)', strokeWidth: 1 }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.[0]) return null
                    const p = payload[0].payload as ChartDataPoint
                    return (
                      <div className="bg-white border border-gray-200 rounded shadow-sm px-3 py-1.5 text-small">
                        <div className="font-medium text-gray-800">{p.label}</div>
                        <div className="text-gray-600">{fmt(p.value)}</div>
                      </div>
                    )
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: '#6366f1' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : def.chartType === 'bar' ? (
          <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={fmt}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  content={({ active, payload }) => {
                    if (!active || !payload?.[0]) return null
                    const p = payload[0].payload as ChartDataPoint
                    return (
                      <div className="bg-white border border-gray-200 rounded shadow-sm px-3 py-1.5 text-small">
                        <div className="font-medium text-gray-800">{p.label}</div>
                        <div className="text-gray-600">{fmt(p.value)}</div>
                      </div>
                    )
                  }}
                />
                <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={40}>
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : def.chartType === 'donut' ? (
          <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius="55%"
                  outerRadius="80%"
                  paddingAngle={2}
                >
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.[0]) return null
                    const p = payload[0].payload as ChartDataPoint
                    return (
                      <div className="bg-white border border-gray-200 rounded shadow-sm px-3 py-1.5 text-small">
                        <div className="font-medium text-gray-800">{p.label}</div>
                        <div className="text-gray-600">{fmt(p.value)}</div>
                      </div>
                    )
                  }}
                />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  formatter={(value) => <span className="text-xs text-gray-600">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : null}
      </Card.Body>
    </Card>
  )
}
